import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import InputMask from 'react-input-mask';
import { MdAccessTime, MdCheckCircle, MdClose, MdLanguage, MdLocalOffer, MdSmartphone, MdThumbUp, MdWhatsapp } from 'react-icons/md';
import { obterNomeEmpresa } from '../../services/seguranca';
import { toast } from 'react-toastify';
import api from '../../services/api';
import {
    OFERTAS,
    Oferta,
    ProdutoOferta,
    deveAbrirHoje,
    elegivel,
    existeModalAberto,
    hojeISO,
    linkWhatsAppOferta,
    marcarAberturaHoje,
    mensagemDoErro,
    ofertaAtiva,
    telefoneValido,
    tempoRestante,
    textoContador,
} from './ofertas';
import {
    Aba,
    Abas,
    Acoes,
    Botao,
    BotaoInteresse,
    BotaoWhatsApp,
    Cabecalho,
    CampoTelefone,
    Confirmacao,
    Contador,
    Corpo,
    Etiqueta,
    Fechar,
    Kicker,
    LinhaDestaque,
    Mini,
    MiniIcone,
    Nota,
    Painel,
    Paragrafo,
    Rodape,
    SeloHeader,
    TagOferta,
    TagSelo,
    Texto,
    Tile,
    TileIcone,
    Titulo,
} from './styles';

export const EVENTO_ABRIR_OFERTAS = 'abrir-ofertas-black-friday';

const ESPERA_INICIAL_MS = 3000;
const ESPERA_ENTRE_TENTATIVAS_MS = 2000;
const ESPERA_MAXIMA_MS = 60000;
const TEMPO_MINIMO_EXIBICAO_MS = 3000;
const INTERVALO_CONTADOR_MS = 30000;

type OrigemAbertura = 'automatica' | 'selo';
type OrigemFechamento = 'x' | 'agora_nao' | 'fundo';

export function ofertasVisiveis(token: string): boolean {
    return elegivel(token) && ofertaAtiva(new Date());
}

export function SeloOfertasBlackFriday({ onClick }: { onClick: () => void }) {
    return (
        <SeloHeader type="button" onClick={onClick} title="Ofertas Black Friday">
            <MdLocalOffer size={14} />
            Ofertas Black Friday
            <TagSelo>30%</TagSelo>
        </SeloHeader>
    );
}

function IconeOferta({ oferta, tamanho }: { oferta: Oferta; tamanho: number }) {
    if (oferta.icone === 'smartphone') return <MdSmartphone size={tamanho} />;
    return <MdLanguage size={tamanho} />;
}

function TextoComNumerosEmDestaque({ texto }: { texto: string }) {
    return <>{texto.split(/(\d+)/).map((parte, i) => (/^\d+$/.test(parte) ? <b key={i}>{parte}</b> : parte))}</>;
}

function registrarInteracao(evento: string, produto?: ProdutoOferta, segundos?: number) {
    api.post('/OfertaBlackFriday/Interacao', { evento, produto, segundos }).catch(() => {});
}

interface Props {
    token: string;
    telefoneEmpresa: string | null;
    pathname: string;
}

export default function OfertasBlackFriday({ token, telefoneEmpresa, pathname }: Props) {
    const [aberto, setAberto] = useState(false);
    const [podeFechar, setPodeFechar] = useState(false);
    const [agora, setAgora] = useState(() => new Date());
    const [abaAtiva, setAbaAtiva] = useState<ProdutoOferta>(OFERTAS[0].produto);
    const [confirmando, setConfirmando] = useState<ProdutoOferta | null>(null);
    const [telefone, setTelefone] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [enviados, setEnviados] = useState<Set<ProdutoOferta>>(new Set());
    const timersAbertura = useRef<number[]>([]);
    const abertoEm = useRef<number>(0);

    const visivel = ofertasVisiveis(token);

    const limparTimersAbertura = () => {
        timersAbertura.current.forEach(t => window.clearTimeout(t));
        timersAbertura.current = [];
    };

    const abrir = useCallback((origem: OrigemAbertura) => {
        limparTimersAbertura();
        marcarAberturaHoje(hojeISO(new Date()), localStorage);
        abertoEm.current = Date.now();
        setPodeFechar(false);
        setAgora(new Date());
        setConfirmando(null);
        setTelefone(telefoneEmpresa || '');
        setAberto(true);
        registrarInteracao(`abertura_${origem}`);
        api.get('/OfertaBlackFriday/Interesses')
            .then(res => setEnviados(new Set<ProdutoOferta>(res.data?.produtos || [])))
            .catch(() => {});
    }, [telefoneEmpresa]);

    useEffect(() => {
        if (!aberto) return;
        const liberar = window.setTimeout(() => setPodeFechar(true), TEMPO_MINIMO_EXIBICAO_MS);
        const relogio = window.setInterval(() => setAgora(new Date()), INTERVALO_CONTADOR_MS);
        return () => {
            window.clearTimeout(liberar);
            window.clearInterval(relogio);
        };
    }, [aberto]);

    const fechar = (origem: OrigemFechamento) => {
        if (!podeFechar) return;
        const segundos = Math.round((Date.now() - abertoEm.current) / 1000);
        registrarInteracao(`fechamento_${origem}`, abaAtiva, segundos);
        setAberto(false);
    };

    useEffect(() => {
        if (!visivel) return;
        const aoPedir = () => abrir('selo');
        window.addEventListener(EVENTO_ABRIR_OFERTAS, aoPedir);
        return () => window.removeEventListener(EVENTO_ABRIR_OFERTAS, aoPedir);
    }, [visivel, abrir]);

    useEffect(() => {
        limparTimersAbertura();
        if (!visivel || pathname !== '/home') return;
        const hoje = hojeISO(new Date());
        if (!deveAbrirHoje(hoje, localStorage)) return;

        const inicio = Date.now();
        const tentar = () => {
            if (existeModalAberto(document)) {
                if (Date.now() - inicio < ESPERA_MAXIMA_MS) {
                    timersAbertura.current.push(window.setTimeout(tentar, ESPERA_ENTRE_TENTATIVAS_MS));
                }
                return;
            }
            if (!deveAbrirHoje(hoje, localStorage)) return;
            abrir('automatica');
        };
        timersAbertura.current.push(window.setTimeout(tentar, ESPERA_INICIAL_MS));
        return limparTimersAbertura;
    }, [visivel, pathname, abrir]);

    if (!visivel) return null;

    const trocarAba = (produto: ProdutoOferta) => {
        setAbaAtiva(produto);
        setConfirmando(null);
        registrarInteracao('aba', produto);
    };

    const iniciarContato = (produto: ProdutoOferta) => {
        setConfirmando(produto);
        registrarInteracao('contato_iniciado', produto);
    };

    const confirmar = async (produto: ProdutoOferta) => {
        if (enviando || !telefoneValido(telefone)) return;
        setEnviando(true);
        try {
            await api.post('/OfertaBlackFriday/Interesse', { produto, telefone });
            setEnviados(prev => new Set(prev).add(produto));
            setConfirmando(null);
        } catch (err: any) {
            toast.error(mensagemDoErro(err, 'Não foi possível registrar seu interesse agora.'));
        } finally {
            setEnviando(false);
        }
    };

    const contador = textoContador(tempoRestante(agora));

    return (
        <Modal show={aberto} onHide={() => fechar('fundo')} backdrop={podeFechar ? true : 'static'} keyboard={podeFechar} centered size="lg">
            <Cabecalho>
                <Fechar type="button" aria-label="Fechar" disabled={!podeFechar} onClick={() => fechar('x')}><MdClose /></Fechar>
                <Kicker><MdLocalOffer size={13} /> Black Friday LocaSmartPro</Kicker>
                <Titulo>30% de desconto em qualquer solução</Titulo>
                <Paragrafo>Assistente de WhatsApp, App e Site personalizados e LocaSmartCheck. Oportunidade para ativar o que faltava na sua locadora.</Paragrafo>
                <LinhaDestaque>
                    <Etiqueta>A hora é agora!</Etiqueta>
                    <Contador>
                        <MdAccessTime size={15} />
                        <span><TextoComNumerosEmDestaque texto={contador} /></span>
                    </Contador>
                </LinhaDestaque>
            </Cabecalho>
            <Corpo>
                <Abas>
                    {OFERTAS.map(oferta => (
                        <Aba key={oferta.produto} type="button" $ativa={abaAtiva === oferta.produto} onClick={() => trocarAba(oferta.produto)}>
                            {oferta.imagem
                                ? <Mini src={oferta.imagem} alt="" />
                                : <MiniIcone><IconeOferta oferta={oferta} tamanho={12} /></MiniIcone>}
                            <span className="titulo">{oferta.titulo}</span>
                        </Aba>
                    ))}
                </Abas>

                {OFERTAS.filter(o => o.produto === abaAtiva).map(oferta => {
                    const enviado = enviados.has(oferta.produto);
                    const emConfirmacao = confirmando === oferta.produto;
                    return (
                        <Painel key={oferta.produto}>
                            {oferta.imagem
                                ? <Tile><img src={oferta.imagem} alt="" /></Tile>
                                : <TileIcone><IconeOferta oferta={oferta} tamanho={52} /></TileIcone>}
                            <Texto>
                                <h3>{oferta.titulo}</h3>
                                <p>{oferta.texto}</p>
                                <TagOferta><MdLocalOffer size={14} /> 30% de desconto na ativação</TagOferta>
                                {enviado ? (
                                    <Acoes>
                                        <Confirmacao><MdCheckCircle size={16} /> Recebemos seu interesse. Vamos te chamar.</Confirmacao>
                                        <BotaoWhatsApp
                                            href={linkWhatsAppOferta(oferta.produto, obterNomeEmpresa(token))}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => registrarInteracao('whatsapp_aberto', oferta.produto)}>
                                            <MdWhatsapp /> Chamar no WhatsApp agora
                                        </BotaoWhatsApp>
                                        <Nota>Mandando a mensagem agora, a equipe já responde na mesma conversa.</Nota>
                                    </Acoes>
                                ) : emConfirmacao ? (
                                    <CampoTelefone>
                                        <label>
                                            WhatsApp para contato
                                            <InputMask
                                                mask="(99) 99999-9999"
                                                value={telefone}
                                                onChange={e => setTelefone(e.target.value)}
                                                disabled={enviando}
                                                placeholder="(00) 00000-0000"
                                                autoFocus
                                            />
                                        </label>
                                        <Botao type="button" disabled={enviando || !telefoneValido(telefone)} onClick={() => confirmar(oferta.produto)}>
                                            {enviando ? 'Enviando...' : 'Confirmar'}
                                        </Botao>
                                    </CampoTelefone>
                                ) : (
                                    <Acoes>
                                        <BotaoInteresse type="button" onClick={() => iniciarContato(oferta.produto)}><MdThumbUp /> Eu tenho interesse!</BotaoInteresse>
                                        <Nota>Nossa equipe fala com você pelo WhatsApp da empresa.</Nota>
                                    </Acoes>
                                )}
                            </Texto>
                        </Painel>
                    );
                })}

                <Rodape>
                    <span>Você pode reabrir estas ofertas pelo botão no topo do sistema.</span>
                    <button type="button" disabled={!podeFechar} onClick={() => fechar('agora_nao')}>Agora não</button>
                </Rodape>
            </Corpo>
        </Modal>
    );
}
